import {
  MongoReturnId,
  MongoReturnItemId,
  MongoUserId,
} from "@redotech/redo-model/mongo-ids";
import * as z from "zod/v4";

export const InventoryItemKindSchema = z.enum([
  "PRODUCT",
  "RETURN",
  "UNDECLARED",
  "UNKNOWN",
]);
export type InventoryItemKind = z.infer<typeof InventoryItemKindSchema>;

export const InventoryItemClassSchema = z.enum(["lot", "custom"]);
export type InventoryItemClass = z.infer<typeof InventoryItemClassSchema>;

export const InventoryItemGradeSchema = z.enum(["A", "B", "C", "D", "E"]);
export type InventoryItemGrade = z.infer<typeof InventoryItemGradeSchema>;

export const PASSING_GRADES = new Set<InventoryItemGrade>(["A", "B", "C"]);

export const InventoryItemDispositionSchema = z.enum([
  "restock",
  "liquidate",
  "donate",
  "dispose",
  "return_to_vendor",
]);
export type InventoryItemDisposition = z.infer<
  typeof InventoryItemDispositionSchema
>;

export const InventoryItemEventTypeSchema = z.enum([
  "restocked",
  "picked",
  "shipped",
  "committed",
  "released",
]);
export type InventoryItemEventType = z.infer<
  typeof InventoryItemEventTypeSchema
>;

const InventoryItemRecordSchema_ = z.object({
  id: z.bigint(),
  accountId: z.bigint(),
  kind: InventoryItemKindSchema,
  productId: z.bigint().nullable(),
  returnId: MongoReturnId.fromDbBuffer.nullable(),
  returnItemId: MongoReturnItemId.fromDbBuffer.nullable(),
  locationId: z.bigint(),
  storageLocationId: z.bigint().nullable(),
  quantity: z.bigint(),
  class: InventoryItemClassSchema.nullable(),
  grade: InventoryItemGradeSchema.nullable(),
  disposition: InventoryItemDispositionSchema.nullable(),
  lotNumber: z.string().nullable(),
  expirationDate: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

export interface InventoryItemRecord {
  id: bigint;
  accountId: bigint;
  kind: InventoryItemKind;
  productId: bigint | null;
  returnId: string | null;
  returnItemId: string | null;
  locationId: bigint;
  storageLocationId: bigint | null;
  quantity: bigint;
  class: InventoryItemClass | null;
  grade: InventoryItemGrade | null;
  disposition: InventoryItemDisposition | null;
  lotNumber: string | null;
  expirationDate: Date | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export const InventoryItemRecordSchema: z.ZodType<InventoryItemRecord> =
  InventoryItemRecordSchema_ as z.ZodType<InventoryItemRecord>;

const InventoryItemWithAvailabilitySchema_ = InventoryItemRecordSchema_.extend({
  available: z.bigint(),
  storageLocationName: z.string().nullable(),
});

export interface InventoryItemWithAvailability extends InventoryItemRecord {
  available: bigint;
  storageLocationName: string | null;
}

export const InventoryItemWithAvailabilitySchema: z.ZodType<InventoryItemWithAvailability> =
  InventoryItemWithAvailabilitySchema_ as z.ZodType<InventoryItemWithAvailability>;

export interface CreateInventoryItemInput {
  accountId: bigint;
  kind: InventoryItemKind;
  locationId: bigint;

  // Required for kind=PRODUCT
  productId?: bigint;

  // Required for kind=RETURN (not required for kind=UNKNOWN)
  returnId?: MongoReturnId;

  // Optional
  returnItemId?: MongoReturnItemId;
  storageLocationId?: bigint;
  quantity?: number;
  class?: InventoryItemClass;
  grade?: InventoryItemGrade;
  disposition?: InventoryItemDisposition;

  // Lot tracking
  lotNumber?: string;
  expirationDate?: Date;

  // For event tracking
  userId?: MongoUserId;
}

export interface DeleteInventoryItemInput {
  id: bigint;
  accountId: bigint;
}

export interface CreateInventoryItemEventInput {
  inventoryItemId: bigint;
  eventType: InventoryItemEventType;
  userId?: MongoUserId;
  payload?: Record<string, unknown>;
}
